import { SEO } from '@/components/SEO';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import LocationFAQ from '@/components/locations/LocationFAQ';
import LocationBlogLinks from '@/components/locations/LocationBlogLinks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Phone, Droplets, Sparkles, Home, Filter, Brush, Sun } from 'lucide-react';

interface LocationPageTemplateProps {
  city: string;
  path: string;
  postalCode: string;
  latitude: number;
  longitude: number;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  schemaDescription: string;
  heroText: string;
  localHeading: string;
  localText: string;
  neighborhoods: string[];
  localFactors: string[];
  faqs: { question: string; answer: string }[];
}

const services = [
  { icon: Droplets, title: 'Pressure Washing', description: 'Hot-water cleaning for driveways, patios, walkways, and other hard surfaces.', link: '/services/pressure-washing' },
  { icon: Brush, title: 'Soft Wash', description: 'Low-pressure cleaning for stucco, painted exteriors, and roofs.', link: '/services/soft-wash' },
  { icon: Sparkles, title: 'Window Cleaning', description: 'Streak-free windows for homes and businesses.', link: '/services/window-washing' },
  { icon: Filter, title: 'Gutter Cleaning', description: 'Clear gutters and downspouts before the rainy season.', link: '/services/gutter-cleaning' },
  { icon: Sun, title: 'Solar Panel Cleaning', description: 'Gentle cleaning that removes dust and bird droppings.', link: '/services/solar-panel-cleaning' },
  { icon: Home, title: 'Carpet Cleaning', description: 'Hot water extraction for stains and traffic lanes.', link: '/services/carpet-cleaning' },
];

const LocationPageTemplate = ({
  city, path, postalCode, latitude, longitude, metaTitle, metaDescription, keywords,
  schemaDescription, heroText, localHeading, localText, neighborhoods, localFactors, faqs,
}: LocationPageTemplateProps) => {
  const navigate = useNavigate();

  const locationSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `Ventura Power Washing - ${city}`,
    description: schemaDescription,
    url: `https://www.venturapowerwashing.com${path}`,
    telephone: '(805) 464-7166',
    areaServed: {
      '@type': 'City',
      name: city,
      containedInPlace: { '@type': 'State', name: 'California' },
    },
    geo: { '@type': 'GeoCoordinates', latitude, longitude },
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressRegion: 'CA',
      postalCode,
      addressCountry: 'US',
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '07:00',
      closes: '19:00',
    },
    priceRange: '$$',
    image: 'https://www.venturapowerwashing.com/og-image.jpg',
    sameAs: [
      'https://www.instagram.com/venturacleaningco',
      'https://www.yelp.com/biz/ventura-power-washing-thousand-oaks',
    ],
  };

  return (
    <>
      <SEO title={metaTitle} description={metaDescription} keywords={keywords} path={path}>
        <script type="application/ld+json">{JSON.stringify(locationSchema)}</script>
      </SEO>

      <Navbar />

      <main className="min-h-screen bg-background">
        <div className="container mx-auto px-4 pt-24">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: city, href: path }]} />
        </div>

        <section className="py-16 md:py-24 bg-gradient-to-br from-ventura-blue/10 to-ventura-green/10">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Professional Cleaning Services in <span className="text-ventura-blue">{city}, CA</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8">{heroText}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-ventura-blue hover:bg-ventura-blue/90 text-white" onClick={() => (window.location.href = 'tel:+18054647166')}>
                  <Phone className="mr-2 h-5 w-5" />
                  Call (805) 464-7166
                </Button>
                <Button size="lg" variant="outline" className="border-ventura-blue text-ventura-blue hover:bg-ventura-blue/10" onClick={() => navigate('/free-estimate')}>
                  Get Free Estimate
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12">Our Services in {city}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map(({ icon: Icon, title, description, link }) => (
                <Card key={title} className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className="mb-4"><Icon className="h-8 w-8 text-ventura-blue" /></div>
                    <CardTitle className="text-xl">{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to={link} className="text-ventura-blue hover:text-ventura-blue/80 font-medium inline-flex items-center">Learn More →</Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-foreground mb-6">{localHeading}</h2>
              <p className="text-muted-foreground mb-6">{localText}</p>
              <div className="grid md:grid-cols-2 gap-8 mt-8">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">
                    {neighborhoods.length > 0 ? 'Neighborhoods We Serve' : 'What We See Here'}
                  </h3>
                  <ul className="space-y-2 text-muted-foreground">
                    {(neighborhoods.length > 0 ? neighborhoods : localFactors).map((item) => (
                      <li key={item}>• {item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">Why Choose Us</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Hot-water cleaning and gentle soft wash</li>
                    <li>• Biodegradable, plant-safe, pet-safe detergents</li>
                    <li>• Licensed, with $1M–$2M liability and workers’ comp</li>
                    <li>• Free estimates, quotes within 24 hours</li>
                    <li>• No long-term contracts</li>
                  </ul>
                </div>
              </div>
              {neighborhoods.length > 0 && (
                <ul className="mt-8 space-y-2 text-muted-foreground">
                  {localFactors.map((item) => <li key={item}>• {item}</li>)}
                </ul>
              )}
              <div className="mt-8 text-center">
                <Button size="lg" className="bg-ventura-blue hover:bg-ventura-blue/90 text-white" onClick={() => navigate('/free-estimate')}>
                  Get Your Free {city} Estimate
                </Button>
              </div>
            </div>
          </div>
        </section>

        <LocationFAQ city={city} faqs={faqs} />
        <LocationBlogLinks city={city} />
      </main>

      <Footer />
    </>
  );
};

export default LocationPageTemplate;
